import { useState, useRef, useCallback, useEffect } from "react";
import {
  ApiError,
  apiErrorDetail,
  type ArtifactPublicView,
  type Visibility,
  api,
} from "../lib/api";
import { ArtifactCardMenu } from "./ArtifactCardMenu";

interface Props {
  /** The artifact to render. */
  artifact: ArtifactPublicView;
  /** True if the current user owns this artifact (controls ⋯ menu). */
  ownedByMe: boolean;
  /** Always show the owner email in the inline summary, even when ownedByMe. */
  showOwner?: boolean;
  /** Callback after a delete or renew so the parent can re-fetch. */
  onChange?: () => void;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(s: string): string {
  return new Date(s).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatRelative(s: string | null): string {
  if (!s) return "Never";
  const ms = Date.now() - new Date(s).getTime();
  const days = Math.round(ms / (1000 * 60 * 60 * 24));
  const hours = Math.round(ms / (1000 * 60 * 60));
  if (hours < 1) return "just now";
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return "1d ago";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.round(days / 7)}w ago`;
  return formatDate(s);
}

const VISIBILITY_LABEL: Record<Visibility, string> = {
  private: "Private",
  unlisted: "Anyone with link",
  shared: "Shared with org",
};

const VISIBILITY_HINT: Record<Visibility, string> = {
  private: "Only you and people you share it with can view this artifact.",
  unlisted: "Any DataCamper with the URL can view. Not listed or searchable.",
  shared: "Listed in 'Shared with org' and findable via MCP search.",
};

const VISIBILITY_CHIP_CLASS: Record<Visibility, string> = {
  private: "bg-slate-100 text-slate-700",
  unlisted: "bg-blue-100 text-blue-800",
  shared: "bg-emerald-100 text-emerald-800",
};

/** Outcome of a tag PATCH, already reconciled against the server. */
export interface TagCommitResult {
  /** Server-truth tag list to render. */
  tags: string[];
  /** Server-truth ``edit_version`` to send with the next write. */
  editVersion: number;
  /** Server detail on conflict, else null. */
  error: string | null;
  /**
   * True when the conflict was an advisory edit lease held by someone else —
   * a refetch does not cure it (the lease outlives the request), so the card
   * must surface the server detail (it names the holder) rather than a
   * generic "reloaded, try again". A version-mismatch 409 or the 422 token
   * mandate is `false`: those the refetch does resolve, and their server
   * detail is MCP-flavoured jargon a card user can't act on.
   */
  leaseHeld: boolean;
}

// Marker in the backend's transport-neutral lease-409 detail
// (write_guard.guard_write). Display-only coupling: the worst case if the
// wording drifts is showing the generic line instead of the holder's name.
const LEASE_CONFLICT_MARKER = /checked out for editing by/i;

/**
 * PATCH the tag list and reconcile the result with the server.
 *
 * A 409 (stale ``if_version``, or an advisory lease held by someone else) and
 * a 422 (the token mandate, hit by any client that did not send one) both mean
 * "your view of this artifact is not the server's" — so both refetch instead of
 * rolling back to the last value *this* client saw, which is exactly what went
 * stale.
 *
 * The refetched state is authoritative for a version/422 conflict. A lease-held
 * 409 is NOT cured by refetching (`leaseHeld: true`) — the caller surfaces the
 * server detail so the user can coordinate with the named holder. Note: today
 * the tag editor is owner-gated and the owner overrides the lease, so a
 * lease-409 is unreachable HERE; the classification exists for correctness and
 * for when the tag UI is extended to editor rows.
 *
 * Any other failure (including a failed refetch) throws, leaving the caller to
 * roll back.
 */
export async function commitTags(
  slug: string,
  next: string[],
  ifVersion: number,
): Promise<TagCommitResult> {
  try {
    const updated = await api.setTags(slug, next, ifVersion);
    return {
      tags: updated.tags ?? [],
      editVersion: updated.edit_version,
      error: null,
      leaseHeld: false,
    };
  } catch (e) {
    if (!(e instanceof ApiError) || (e.status !== 409 && e.status !== 422)) {
      throw e;
    }
    const detail = apiErrorDetail(e);
    const fresh = await api.getOne(slug);
    return {
      tags: fresh.tags ?? [],
      editVersion: fresh.edit_version,
      error: detail,
      leaseHeld: e.status === 409 && LEASE_CONFLICT_MARKER.test(detail),
    };
  }
}

/** One artifact in the unified grid. Compact by default, expandable for meta. */
export function ArtifactCard({
  artifact,
  ownedByMe,
  showOwner = false,
  onChange,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tags, setTags] = useState<string[]>(artifact.tags ?? []);
  const [tagInput, setTagInput] = useState("");
  const [addingTag, setAddingTag] = useState(false);
  const [tagError, setTagError] = useState("");
  const [isPatching, setIsPatching] = useState(false);
  // The optimistic-concurrency token sent with every tag write, in a REF —
  // not state — so two rapid edits can't send the same stale token before a
  // re-render (that would 409 the user against their own first write). Not
  // rendered, so a ref costs no re-render and removes the ordering landmine.
  const addingRef = useRef(false);
  const patchSeqRef = useRef(0);
  const serverTagsRef = useRef<string[]>(artifact.tags ?? []);
  const editVersionRef = useRef(artifact.edit_version);

  useEffect(() => {
    if (!addingTag) {
      serverTagsRef.current = artifact.tags ?? [];
      setTags(artifact.tags ?? []);
      editVersionRef.current = artifact.edit_version;
    }
  }, [artifact.tags, artifact.edit_version, addingTag]);

  const TAG_RE = /^[a-z0-9-]{1,30}$/;

  const patchTags = useCallback(
    async (next: string[]) => {
      const seq = ++patchSeqRef.current;
      setIsPatching(true);
      setTags(next);
      try {
        const result = await commitTags(
          artifact.slug,
          next,
          editVersionRef.current,
        );
        // The ref updates unconditionally so a later edit always sends the
        // freshest token; the seq guard still protects the rendered state
        // from a superseded late response.
        editVersionRef.current = result.editVersion;
        serverTagsRef.current = result.tags;
        if (patchSeqRef.current === seq) {
          setTags(result.tags);
          // Lease conflict: show the server detail (it names the holder) —
          // a refetch can't cure it. Version/422 conflict: the raw detail is
          // MCP jargon ("re-read the source, retry with if_version"), so show
          // a card-appropriate line; the tags were already refreshed above.
          setTagError(
            result.leaseHeld
              ? (result.error ?? "")
              : result.error
                ? "This artifact changed since you loaded it — tags were refreshed. Try again."
                : "",
          );
          // Refresh the parent ONLY on success. On a conflict, onChange
          // (the page's reload) would setItems(null) and unmount this card,
          // discarding the error we just set — the card has already refetched
          // its own server-truth tags/token via commitTags, so its local
          // state is correct without a parent reload.
          if (!result.error) onChange?.();
        }
      } catch {
        if (patchSeqRef.current === seq) {
          setTags(serverTagsRef.current);
        }
      } finally {
        setIsPatching(false);
      }
    },
    [artifact.slug, onChange],
  );

  function removeTag(tag: string) {
    void patchTags(tags.filter((t) => t !== tag));
  }

  async function commitTagInput() {
    if (addingRef.current) return;
    addingRef.current = true;
    const val = tagInput.trim();
    if (!val) {
      setAddingTag(false);
      setTagInput("");
      addingRef.current = false;
      return;
    }
    if (!TAG_RE.test(val)) {
      setTagError("lowercase, a–z 0–9 hyphens, max 30 chars");
      addingRef.current = false;
      return;
    }
    if (tags.includes(val)) {
      setAddingTag(false);
      setTagInput("");
      addingRef.current = false;
      return;
    }
    setTagInput("");
    setTagError("");
    setAddingTag(false);
    await patchTags([...tags, val]);
    addingRef.current = false;
  }

  async function copy() {
    await navigator.clipboard.writeText(artifact.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }

  const expiresIn = Math.max(
    0,
    Math.round(
      (new Date(artifact.expires_at).getTime() - Date.now()) /
        (1000 * 60 * 60 * 24),
    ),
  );

  const expiresLabel = artifact.permanent
    ? "Never expires"
    : `${expiresIn}d left`;
  const expiresClass =
    !artifact.permanent && expiresIn <= 7
      ? "font-medium text-amber-700"
      : "text-slate-500";

  const showOwnerLine = showOwner || !ownedByMe;
  // Strip the @datacamp.com suffix in the inline summary — saves space and
  // every account is @datacamp.com anyway.
  const ownerShort = artifact.owner_email.replace(/@datacamp\.com$/, "@");

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      {/* Header: title (line-clamp-2) + slug as a small mono caption */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-base font-semibold leading-snug">
            {artifact.title}
          </h3>
          {artifact.description && (
            <p className="mt-1 line-clamp-2 text-sm text-slate-500">
              {artifact.description}
            </p>
          )}
        </div>
        <span className="shrink-0 font-mono text-xs text-slate-400">
          {artifact.slug}
        </span>
      </div>

      {/* Visibility / permanent chips */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${VISIBILITY_CHIP_CLASS[artifact.visibility]}`}
          title={VISIBILITY_HINT[artifact.visibility]}
        >
          {VISIBILITY_LABEL[artifact.visibility]}
        </span>
        {artifact.permanent && (
          <span
            className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800"
            title="Pinned: the daily purge sweep skips this artifact regardless of expires_at."
          >
            Permanent
          </span>
        )}
        {artifact.shared_with_count > 0 && (
          <span
            className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800"
            title="People this artifact is directly shared with (they can view it even while private)."
          >
            Shared with {artifact.shared_with_count}
          </span>
        )}
      </div>

      {/* Inline summary: views · expires · owner */}
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm">
        <span className="text-slate-700">
          <span className="font-semibold text-slate-900">
            {artifact.view_count}
          </span>{" "}
          {artifact.view_count === 1 ? "view" : "views"}
        </span>
        <span className="text-slate-300" aria-hidden>
          ·
        </span>
        <span className={expiresClass}>{expiresLabel}</span>
        {showOwnerLine && (
          <>
            <span className="text-slate-300" aria-hidden>
              ·
            </span>
            <span className="text-slate-500" title={artifact.owner_email}>
              {ownerShort}
            </span>
          </>
        )}
      </div>

      {/* Actions row */}
      <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <a
            href={artifact.url}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-700"
          >
            Open
          </a>
          <button
            type="button"
            onClick={copy}
            className="rounded-md border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50"
          >
            {copied ? "Copied!" : "Copy link"}
          </button>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setExpanded((p) => !p)}
            aria-expanded={expanded}
            className="rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            {expanded ? "▴ Less" : "Details ▾"}
          </button>
          {/* Owners get the full menu; editors get their slice of it
              (Share… read-only + Versions) — the broadened sharing GET and
              version access need a browser surface. Plain viewers get none. */}
          {(ownedByMe || artifact.access === "editor") && (
            <ArtifactCardMenu artifact={artifact} onChange={onChange} />
          )}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <dl className="mt-1 grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 border-t border-dashed border-slate-200 pt-3 text-xs text-slate-500">
          <dt>Created</dt>
          <dd className="text-right text-slate-700">
            {formatDate(artifact.created_at)}
          </dd>
          <dt>Size</dt>
          <dd className="text-right text-slate-700">
            {formatBytes(artifact.size_bytes)}
          </dd>
          <dt>Last viewed</dt>
          <dd className="text-right text-slate-700">
            {formatRelative(artifact.last_viewed_at)}
          </dd>
          <dt className="col-span-2 mt-2 border-t border-dashed border-slate-100 pt-2">
            Tags
          </dt>
          <dd className="col-span-2">
            {ownedByMe ? (
              <div className="flex flex-wrap items-center gap-1.5">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-800"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      disabled={isPatching}
                      className="leading-none text-violet-500 hover:text-violet-900 disabled:opacity-40"
                      aria-label={`Remove tag ${tag}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                {tags.length < 10 && !addingTag && !isPatching && (
                  <button
                    type="button"
                    onClick={() => {
                      setAddingTag(true);
                      setTagError("");
                    }}
                    className="rounded-full border border-dashed border-violet-300 px-2 py-0.5 text-xs text-violet-600 hover:border-violet-500"
                  >
                    + add
                  </button>
                )}
                {addingTag && (
                  <div className="flex items-center gap-1">
                    <input
                      autoFocus
                      type="text"
                      value={tagInput}
                      onChange={(e) => {
                        setTagInput(e.target.value);
                        setTagError("");
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          void commitTagInput();
                        }
                        if (e.key === "Escape") {
                          setAddingTag(false);
                          setTagInput("");
                          setTagError("");
                        }
                      }}
                      onBlur={() => void commitTagInput()}
                      placeholder="e.g. bi-tool"
                      className={`w-28 rounded border px-1.5 py-0.5 text-xs focus:outline-none ${
                        tagError
                          ? "border-red-400 focus:ring-1 focus:ring-red-400"
                          : "border-violet-300 focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
                      }`}
                    />
                  </div>
                )}
                {tagError && (
                  <span className="text-xs text-red-500">{tagError}</span>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {tags.length > 0 ? (
                  tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-800"
                    >
                      {tag}
                    </span>
                  ))
                ) : (
                  <span className="text-slate-400">&mdash;</span>
                )}
              </div>
            )}
          </dd>
        </dl>
      )}
    </div>
  );
}
